import { IImageDimension, IViewPort } from '@msdyn365-commerce/core-internal';
import { useMemo } from 'react';

// Standard set of image sizes requested from the CMS, shared across all pages to leverage image caching.
export type StandardImageSize = 'small' | 'medium' | 'large' | 'navMenuSmall' | 'navMenuMedium' | 'navMenuLarge';

/*
 * - Images use a 3:4 ratio, except for the nav menu, which uses a 2:3 ratio.
 * - Sizes must be x2 the intended display size so they look sharp on high-DPR screens.
 * - When consuming, for each breakpoint you must pick a larger size than the rendered size multiplied by 2 (DPR adjustment)
 *
 * - Caveat: Our product images are not big enough to be requested in larger sizes than these.
 *   Some pages cannot show fully scaled images on high dpr screens (for example PDP and PLP on a mac computer).
 */
const STANDARD_IMAGE_SIZES: Record<StandardImageSize, Pick<IImageDimension, 'h' | 'w'>> = {
    // Global sizes
    small: { w: 660, h: 880 },
    medium: { w: 840, h: 1120 },
    large: { w: 1125, h: 1500 },

    // Nav menu sizes
    navMenuSmall: { w: 380, h: 570 },
    navMenuMedium: { w: 512, h: 766 },
    navMenuLarge: { w: 660, h: 990 }
};

export type BaseImageSettings = {
    [K in keyof IViewPort]: StandardImageSize;
};

const getParsedImageViewportSettings = (simpleSettings: BaseImageSettings): IViewPort => {
    return Object.entries(simpleSettings).reduce((acc, [key, sizeName]) => {
        const { w, h } = STANDARD_IMAGE_SIZES[sizeName];
        return { ...acc, [key]: { w, h, q: `w=${w}&h=${h}&m=6&f=webp&q=80` } };
    }, {} as IViewPort);
};

export const useImageSettings = (baseSettings: BaseImageSettings) => {
    const imageViewportSettings = useMemo(() => getParsedImageViewportSettings(baseSettings), [baseSettings]);

    return imageViewportSettings;
};
